import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { Router } from '@angular/router';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { AdminDndEmail } from '../models/dnd-email';
import { DndEmailService } from '../services/dnd-email.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-dnd-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    PaginationComponent,
    PageSizeComponent,
    SvgIconComponent,
    PopupComponent,
    SearchInputComponent,
  ],
  templateUrl: './dnd-list.component.html',
  styleUrl: './dnd-list.component.scss',
})
export class DndListComponent extends MenuComponentBase implements OnInit {
  dndEmailService = inject(DndEmailService);
  router = inject(Router);
  private commonService = inject(CommonService);

  loading = false;
  totalDndEmails = 0;
  dndEmails: AdminDndEmail[] = [];
  newEmail = '';
  newReason = '';
  isDndPopupVisible = false;
  searchQuery = '';
  showDeleteDndConfirmPopup = false;
  selectedDndEmail: { id: number; email: string } | null = null;

  currentPage = 1;
  pageSize = 50;
  tableColumns = [
    { header: 'Email', width: '25%' },
    { header: 'Reason', width: '25%' },
    { header: 'Added On', width: '25%' },
    { header: 'Actions', width: '25%' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadDndEmails();
  }

  loadDndEmails(): void {
    const search = this.searchQuery || undefined;
    this.loading = true;

    this.dndEmailService
      .getDndEmails(this.currentPage, this.pageSize, search)
      .subscribe({
        next: (res) => {
          this.dndEmails = res.results;
          this.totalDndEmails = res.count;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  addDndEmail(): void {
    if (!this.newEmail.trim()) return;

    this.dndEmailService
      .addDndEmail({
        email: this.newEmail,
        reason: this.newReason,
      })
      .subscribe({
        next: () => {
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Email added to DND list successfully',
          });

          this.newEmail = '';
          this.newReason = '';
          this.isDndPopupVisible = false;
          this.loadDndEmails();
        },
        error: ({ error }) =>
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail || error?.[0] || 'Failed to add email to DND list',
          }),
      });
  }

  openDeleteDndPopup(dndEmail: { id: number; email: string }): void {
    this.selectedDndEmail = dndEmail;
    this.showDeleteDndConfirmPopup = true;
  }

  confirmDeleteDndEmail(): void {
    if (!this.selectedDndEmail) return;

    this.dndEmailService.deleteDndEmail(this.selectedDndEmail.id).subscribe({
      next: () => {
        this.loadDndEmails();
        this.closeDeleteDndPopup();
      },
      error: (err) => {
        console.error('Error deleting DND email:', err);
        this.closeDeleteDndPopup();
      },
    });
  }

  closeDeleteDndPopup(): void {
    this.showDeleteDndConfirmPopup = false;
    this.selectedDndEmail = null;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadDndEmails();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadDndEmails();
    }
  }

  isValidEmail(email: string | null | undefined): boolean {
    if (!email) return false;

    const trimmed = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(trimmed);
  }

  isValidReason(reason: string | null | undefined): boolean {
    if (!reason) return false;

    const trimmed = reason.trim();

    // must not be empty
    if (!trimmed) return false;

    // must not be only numbers
    const onlyNumbers = /^\d+$/.test(trimmed);
    if (onlyNumbers) return false;

    return true;
  }

  get isDndFormValid(): boolean {
    return (
      this.isValidEmail(this.newEmail) && this.isValidReason(this.newReason)
    );
  }

  isReasonOnlyNumbers(reason: string | null | undefined): boolean {
    if (!reason) return false;
    return /^\d+$/.test(reason.trim());
  }

  onAddClick(): void {
    this.isDndPopupVisible = true;
  }

  onCancelDndPopup(): void {
    this.isDndPopupVisible = false;
    this.newEmail = '';
    this.newReason = '';
  }

  onConfirmAddDndEmail(): void {
    this.addDndEmail();
  }

  onSearchQueryChange(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadDndEmails();
  }
}
